// src/pages/PrivacyPolicy.js
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import "./privacy.css";
import Translate from "../components/Navbar/translate/Translate";

import logoClient from "../images/logo.png";
import logoStaff from "../images/logostaff.png";
import {
  SocialMedia,
  SocialMediaWrapper,
  WebsiteRights,
} from "../components/Footer/FooterElements";

const ACCENT_CLIENT = "#18E68E";
const ACCENT_STAFF = "#F9A53C";

/* styled */
const Page = styled.main`
  padding: 96px 20px 40px;
  max-width: 980px;
  margin: 0 auto;
  color: #e5e7eb;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px 0;
  max-width: 980px;
  margin: 0 auto;
  direction: inherit;
`;

const Logo = styled.img`
  width: 140px;
  height: 140px;
  object-fit: contain;
  margin-top: ${(p) => p.mt ?? 0}px;
  margin-inline-start: ${(p) => p.ml ?? 0}px; /* logique */
  @media (max-width: 640px) {
    width: 120px;
    height: 120px;
    margin-inline-start: 0;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  color: ${(p) => p.accent || ACCENT_CLIENT};
  font-weight: 800;
  margin: 0;
  line-height: 1.1;
`;

const Muted = styled.p`
  color: #9ca3af;
  margin: 4px 0 24px;
  font-size: 14px;
  text-align: start;
`;
const Box = styled.section`
  background: #0b1220;
  border: 1px solid #111827;
  border-radius: 14px;
  padding: 18px;
  margin: 18px 0 0;
  direction: inherit; /* hérite LTR/RTL */
  text-align: start; /* gauche en LTR, droite en RTL */

  a {
    color: ${(p) => p.accentLink || "#9dd9b9"};
    text-decoration: underline;
  }
`;

const List = styled.ul`
  margin: 0 0 8px;
  padding-inline-start: 20px; /* en LTR -> à gauche */
  padding-inline-end: 0;
  list-style-position: outside;

  /* Quand le conteneur est en RTL, on inverse proprement le padding */
  &:dir(rtl) {
    padding-inline-start: 0;
    padding-inline-end: 20px; /* en RTL -> à droite */
  }

  li {
    margin: 8px 0;
    line-height: 1.8;
    text-align: start; /* start = gauche LTR / droite RTL */
  }
`;

const Lead = styled.p`
  line-height: 1.7;
  margin-bottom: 18px;
  text-align: start;
`;

const H2 = styled.h2`
  color: ${(p) => p.accent || "#fff"};
  font-size: 20px;
  margin: 0 0 10px;
  font-weight: 800;
  text-align: start;
`;

const H3 = styled.h3`
  color: #fff;
  font-size: 16px;
  margin: 20px 0 8px;
  font-weight: 700;
  text-align: start;
`;

/* Onglets : on aligne à droite en RTL */
const Tabs = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 14px 0 6px;
  justify-content: ${(p) => (p.isRTL ? "flex-end" : "flex-start")};
`;

const TabBtn = styled.button`
  border: 1px solid #1f2937;
  background: ${(p) => (p.active ? p.accent : "#0f172a")};
  color: ${(p) => (p.active ? "#0b1220" : "#e5e7eb")};
  padding: 10px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease-in-out, color 0.2s ease-in-out;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  outline: none;
`;

/* component */
export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState("client");
  const isStaff = tab === "staff";
  const accent = isStaff ? ACCENT_STAFF : ACCENT_CLIENT;

  const isRTL = i18n.dir() === "rtl";
  const lastUpdated = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  return (
    /* on met aussi dir ici pour que tout suive même si <html> n'est pas à jour */
    <div className="privacy-page">
      <TopBar>
        <Logo
          src={isStaff ? logoStaff : logoClient}
          alt={isStaff ? "CliClaim Staff" : "CliClaim"}
          mt={-10}
          ml={0}
        />
        <Translate />
      </TopBar>

      <Page dir={isRTL ? "rtl" : "ltr"}>
        <Header>
          <TitleWrap>
            <Title accent={accent}>{t("privacy.title")}</Title>
            <Muted>
              {t("common.last_updated")}: {lastUpdated} •{" "}
              {t("privacy.applies_to")}
            </Muted>
          </TitleWrap>
        </Header>

        {/* Onglets alignés à droite si RTL */}
        <Tabs role="tablist" aria-label={t("privacy.aria_tablist")}>
          <TabBtn
            type="button"
            role="tab"
            aria-selected={tab === "client"}
            aria-controls="panel-client"
            id="tab-client"
            active={tab === "client"}
            onClick={() => setTab("client")}
            accent={ACCENT_CLIENT}
          >
            {t("common.client_app")}
          </TabBtn>

          <TabBtn
            type="button"
            role="tab"
            aria-selected={tab === "staff"}
            aria-controls="panel-staff"
            id="tab-staff"
            active={tab === "staff"}
            onClick={() => setTab("staff")}
            accent={ACCENT_STAFF}
          >
            {t("common.staff_app")}
          </TabBtn>
        </Tabs>

        {/* 1. Introduction */}
        <Box accentLink={accent}>
          <H2 accent={accent}>{t("privacy.intro_h2")}</H2>
          <Lead dangerouslySetInnerHTML={{ __html: t("privacy.intro_p") }} />
        </Box>

        {/* 2. Réclamation */}
        <Box accentLink={accent}>
          <H2 accent={accent}>{t("privacy.claim_h2")}</H2>
          <Lead>{t("privacy.claim_p")}</Lead>
        </Box>

        {/* 3. Définitions & termes clés */}
        <Box accentLink={accent}>
          <H2 accent={accent}>{t("privacy.defs_h2")}</H2>
          <List>
            <li>
              <b>{t("privacy.defs.cookie_label")}</b>:{" "}
              {t("privacy.defs.cookie_def")}
            </li>
            <li>
              <b>{t("privacy.defs.company_label")}</b>:{" "}
              {t("privacy.defs.company_def")}
            </li>
            <li>
              <b>{t("privacy.defs.country_label")}</b>:{" "}
              {t("privacy.defs.country_def")}
            </li>
            <li>
              <b>{t("privacy.defs.user_label")}</b>:{" "}
              {t("privacy.defs.user_def")}
            </li>
            <li>
              <b>{t("privacy.defs.device_label")}</b>:{" "}
              {t("privacy.defs.device_def")}
            </li>
            <li>
              <b>{t("privacy.defs.ip_label")}</b>: {t("privacy.defs.ip_def")}
            </li>
            <li>
              <b>{t("privacy.defs.staff_label")}</b>:{" "}
              {t("privacy.defs.staff_def")}
            </li>
            <li>
              <b>{t("privacy.defs.personal_label")}</b>:{" "}
              {t("privacy.defs.personal_def")}
            </li>
            <li>
              <b>{t("privacy.defs.service_label")}</b>:{" "}
              {t("privacy.defs.service_def")}
            </li>
            <li>
              <b>{t("privacy.defs.thirdparty_label")}</b>:{" "}
              {t("privacy.defs.thirdparty_def")}
            </li>
            <li>
              <b>{t("privacy.defs.site_label")}</b>:{" "}
              <a href="https://cliclaim.com" target="_blank" rel="noreferrer">
                cliclaim.com
              </a>
            </li>
            <li>
              <b>{t("privacy.defs.you_label")}</b>: {t("privacy.defs.you_def")}
            </li>
          </List>
        </Box>

        {/* 4. Collecte & utilisation */}
        <Box accentLink={accent}>
          <H2 accent={accent}>{t("privacy.collect_h2")}</H2>
          <Lead>{t("privacy.collect_p")}</Lead>

          {tab === "client" && (
            <Box
              id="panel-client"
              role="tabpanel"
              aria-labelledby="tab-client"
              accentLink={ACCENT_CLIENT}
            >
              <H3>{t("privacy.client.h3")}</H3>
              <List>
                <li>
                  <b>{t("privacy.client.you_provide_label")}</b>:{" "}
                  {t("privacy.client.you_provide_def")}
                </li>
                <li>
                  <b>{t("privacy.client.auto_label")}</b>:{" "}
                  {t("privacy.client.auto_def")}
                </li>
                <li>
                  <b>{t("privacy.client.use_share_label")}</b>:{" "}
                  {t("privacy.client.use_share_def")}
                </li>
              </List>
            </Box>
          )}

          {tab === "staff" && (
            <Box
              id="panel-staff"
              role="tabpanel"
              aria-labelledby="tab-staff"
              accentLink={ACCENT_STAFF}
            >
              <H3>{t("privacy.staff.h3")}</H3>
              <List>
                <li>
                  <b>{t("privacy.staff.you_provide_label")}</b>:{" "}
                  {t("privacy.staff.you_provide_def")}
                </li>
                <li>
                  <b>{t("privacy.staff.auto_label")}</b>:{" "}
                  {t("privacy.staff.auto_def")}
                </li>
                <li>
                  <b>{t("privacy.staff.use_share_label")}</b>:{" "}
                  {t("privacy.staff.use_share_def")}
                </li>
              </List>
            </Box>
          )}
        </Box>

        {/* 5. Données de journal */}
        <Box accentLink={accent}>
          <H2 accent={accent}>{t("privacy.logs_h2")}</H2>
          <Lead>{t("privacy.logs_p")}</Lead>
        </Box>

        {/* 6. Cookies */}
        <Box accentLink={accent}>
          <H2 accent={accent}>{t("privacy.cookies_h2")}</H2>
          <Lead>{t("privacy.cookies_p")}</Lead>
        </Box>

        {/* 7. Fournisseurs tiers */}
        <Box accentLink={accent}>
          <H2 accent={accent}>{t("privacy.third_h2")}</H2>
          <List>
            <li>{t("privacy.third_li1")}</li>
            <li>{t("privacy.third_li2")}</li>
            <li>{t("privacy.third_li3")}</li>
          </List>
          <Lead>{t("privacy.third_p")}</Lead>
        </Box>

        {/* 8. Sécurité */}
        <Box accentLink={accent}>
          <H2 accent={accent}>{t("privacy.security_h2")}</H2>
          <List>
            <li>{t("privacy.security_li1")}</li>
            <li>{t("privacy.security_li2")}</li>
            <li>
              {t("privacy.security_li3")}{" "}
              <a href="mailto:contact@cliclaim.com">contact@cliclaim.com</a>
            </li>
          </List>
        </Box>

        {/* 9. Liens externes */}
        <Box accentLink={accent}>
          <H2 accent={accent}>{t("privacy.links_h2")}</H2>
          <Lead>{t("privacy.links_p")}</Lead>
        </Box>

        {/* 10. Vie privée des enfants */}
        <Box accentLink={accent}>
          <H2 accent={accent}>{t("privacy.children_h2")}</H2>
          <Lead>{t("privacy.children_p")}</Lead>
        </Box>

      <SocialMedia>
  <SocialMediaWrapper>
    <Logo
      src={isStaff ? logoStaff : logoClient}
      alt={isStaff ? "CliClaim Staff" : "CliClaim"}
      mt={-10}
      ml={0}
    />
    <WebsiteRights className={isRTL ? "rtl-rights" : ""}>
      <>
        <span style={{ color: "#18e68e" }}>C</span>li
        <span style={{ color: "#18e68e" }}>C</span>laim © {new Date().getFullYear()} {t("footer.title")}.
      </>
    </WebsiteRights>
  </SocialMediaWrapper>
</SocialMedia>
      </Page>
    </div>
  );
}
